import type { ResourcesConfig } from 'aws-amplify';

// Get Cognito configuration from environment variables
const userPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const userPoolClientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
const region = import.meta.env.VITE_AWS_REGION;

// For local development (sandbox), we don't use Cognito
const isSandbox = !userPoolId || !userPoolClientId;

// Sandbox auth is in-memory and provides no real authentication. It is only ever valid for
// local development. Deploying a staging/production build in sandbox mode ships an app that
// cannot authenticate against a real API — this happened once and went unnoticed for months
// because the fallback was silent. Fail loudly instead. See ZM-PH02 B0.
if (isSandbox && import.meta.env.MODE !== 'development' && import.meta.env.MODE !== 'sandbox') {
  throw new Error(
    `Cognito is not configured for build mode '${import.meta.env.MODE}'. ` +
    'VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID are required outside local development. ' +
    `Populate them in .env.${import.meta.env.MODE} from the zenmako-cognito-${import.meta.env.MODE} stack outputs.`
  );
}

// Log auth mode for debugging
console.log('🔐 Auth Mode:', isSandbox ? 'SANDBOX (in-memory)' : 'COGNITO');
console.log('🔐 Cognito Config:', {
  userPoolId: userPoolId || 'not set',
  userPoolClientId: userPoolClientId || 'not set',
  region: region || 'not set'
});

// Validate required config for non-sandbox
if (!isSandbox && !region) {
  throw new Error('VITE_AWS_REGION environment variable is required when using Cognito');
}

export const amplifyConfig: ResourcesConfig = isSandbox
  ? {
      // Sandbox mode - no Cognito configuration
      Auth: {
        Cognito: {
          userPoolId: '',
          userPoolClientId: '',
        },
      },
    }
  : {
      // Production/Staging - use Cognito
      Auth: {
        Cognito: {
          userPoolId: userPoolId!,
          userPoolClientId: userPoolClientId!,
          loginWith: {
            email: true,
          },
          signUpVerificationMethod: 'code',
          userAttributes: {
            email: {
              required: true,
            },
          },
          passwordFormat: {
            minLength: 8,
            requireLowercase: true,
            requireUppercase: true,
            requireNumbers: true,
            requireSpecialCharacters: false,
          },
        },
      },
    };

export const isCognitoEnabled = !isSandbox;
